// src/components/ToastNotification.js
// Toast-style notification component for site-wide announcements

import React, { useEffect, useState } from 'react';
import { CloseOutlined, InfoCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { shouldUseBrandCtaGradient } from '../utils/announcementBrandStyles';
import './ToastNotification.css';

const TOAST_DURATION = 6000; // 6 seconds default
const TOAST_ANIMATION_DURATION = 300; // Animation duration in ms

export default function ToastNotification({ notification, onDismiss, onCTAClick, position = 'top-right' }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const navigate = useNavigate();
    const autoDismissTimer = React.useRef(null);

    useEffect(() => {
        // Trigger entrance animation
        setTimeout(() => setIsVisible(true), 10);

        // Auto-dismiss if duration is set
        if (notification.duration !== 0 && notification.duration !== null) {
            const duration = notification.duration || TOAST_DURATION;
            autoDismissTimer.current = setTimeout(() => {
                handleDismiss();
            }, duration);
        }

        return () => {
            if (autoDismissTimer.current) {
                clearTimeout(autoDismissTimer.current);
            }
        };
    }, []);

    const handleDismiss = () => {
        if (isExiting) return;
        setIsExiting(true);
        setTimeout(() => {
            onDismiss(notification.id);
        }, TOAST_ANIMATION_DURATION);
    };

    const handleCTAClick = () => {
        if (notification.ctaText) {
            if (onCTAClick) {
                onCTAClick(notification.id, notification.ctaLink);
            }

            if (notification.skipNavigate) {
                handleDismiss();
                return;
            }

            if (notification.ctaLink) {
                if (notification.ctaLink.startsWith('/')) {
                    navigate(notification.ctaLink);
                } else {
                    window.open(notification.ctaLink, '_blank');
                }
            }
            handleDismiss();
        }
    };

    // Get icon based on type
    const getIcon = () => {
        switch (notification.type || 'info') {
            case 'success':
                return <CheckCircleOutlined className="toast-icon toast-icon-success" />;
            case 'warning':
                return <WarningOutlined className="toast-icon toast-icon-warning" />;
            case 'error':
                return <ExclamationCircleOutlined className="toast-icon toast-icon-error" />;
            default:
                return <InfoCircleOutlined className="toast-icon toast-icon-info" />;
        }
    };

    const toastClass = `toast-notification ${isVisible && !isExiting ? 'toast-visible' : ''} ${isExiting ? 'toast-exiting' : ''} toast-${position} toast-${notification.type || 'info'}`;

    const ctaBrand = shouldUseBrandCtaGradient(notification.ctaColor);

    return (
        <div className={toastClass}>
            <div className="toast-content">
                {notification.showIcon !== false && (
                    <div className="toast-icon-wrapper">
                        {notification.icon || getIcon()}
                    </div>
                )}
                <div className="toast-body">
                    {notification.title && (
                        <div className="toast-title">{notification.title}</div>
                    )}
                    <div className="toast-message">{notification.message}</div>
                    {notification.ctaText && (
                        <button
                            type="button"
                            className={`toast-cta ${ctaBrand ? 'toast-cta--brand' : ''}`}
                            onClick={handleCTAClick}
                            style={
                                ctaBrand
                                    ? undefined
                                    : {
                                          backgroundColor: notification.ctaColor || '#B99040',
                                          color: '#ffffff',
                                      }
                            }
                        >
                            {notification.ctaText}
                        </button>
                    )}
                </div>
                <button 
                    className="toast-close"
                    onClick={handleDismiss}
                    aria-label="Close notification"
                >
                    <CloseOutlined />
                </button>
            </div>
        </div>
    );
}
